/**
 * DeckEditorPage - Main deck editing view
 *
 * Three-column layout:
 * 1. Left: Deck info panel (name, format, status, validation summary, stats)
 * 2. Center: Cards in deck grouped by zone
 * 3. Right: Card discovery panel
 */
import type { ChangeEvent, KeyboardEvent, ReactElement } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useLocalStorage } from '@uidotdev/usehooks';
import clsx from 'clsx';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  FaArrowLeft,
  FaCheck,
  FaCheckSquare,
  FaEdit,
  FaFileExport,
  FaFileImport,
  FaList,
  FaLock,
  FaLockOpen,
  FaPlus,
  FaTimes,
  FaTrash,
} from 'react-icons/fa';
import { IoCalculator, IoSparkles } from 'react-icons/io5';
import { useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';

import Button from '@/components/Button/Button';
import ConfirmActionModal from '@/components/CardDetails/ConfirmActionModal';
import Dropdown, { type DropdownOption } from '@/components/Dropdown/Dropdown';
import GroupBySelector from '@/components/GroupBySelector/GroupBySelector';
import InlineEditText from '@/components/InlineEditText/InlineEditText';
import KeywordFilterDropdown from '@/components/KeywordFilter/KeywordFilterDropdown';
import Search from '@/components/Search/Search';
import Spinner from '@/components/Spinner/Spinner';
import ViewControlsBar from '@/components/ViewControlsBar/ViewControlsBar';
import DeckAiBadge from '@/features/ai/components/DeckAiBadge';
import DeckPowerModal from '@/features/ai/deckPower/DeckPowerModal';
import { enrichedCollectionAtom } from '@/features/collection/state/collectionAtom';
import BoardSelector from '@/features/decks/components/BoardSelector';
import CardDiscoveryPanel from '@/features/decks/components/CardDiscoveryPanel';
import ColumnVisibilityDropdown from '@/features/decks/components/ColumnVisibilityDropdown';
import DeckBulkActionBar from '@/features/decks/components/DeckBulkActionBar';
import DeckCardDetailsPanel from '@/features/decks/components/DeckCardDetails/DeckCardDetailsPanel';
import DeckGridView from '@/features/decks/components/DeckGridView';
import DeckProbabilityPanel from '@/features/decks/components/DeckProbabilityPanel';
import DeckStackView from '@/features/decks/components/DeckStackView';
import DeckSummaryView from '@/features/decks/components/DeckSummaryView';
import DeckViewToggle from '@/features/decks/components/DeckViewToggle';
import DeleteDeckModal from '@/features/decks/components/DeleteDeckModal';
import ExportDeckModal from '@/features/decks/components/ExportDeckModal';
import ImportDeckModal from '@/features/decks/components/ImportDeckModal';
import RoleFilterDropdown from '@/features/decks/components/RoleFilterDropdown';
import CollapsibleSection from '@/features/decks/components/stats/CollapsibleSection';
import ColorDistributionChart from '@/features/decks/components/stats/ColorDistributionChart';
import ColorSourceAnalysis from '@/features/decks/components/stats/ColorSourceAnalysis';
import CommanderCategories from '@/features/decks/components/stats/CommanderCategories';
import DeckSummaryCard from '@/features/decks/components/stats/DeckSummaryCard';
import DeckWarnings, { type StatsSectionId } from '@/features/decks/components/stats/DeckWarnings';
import LandRecommendation from '@/features/decks/components/stats/LandRecommendation';
import ManaBaseHealth from '@/features/decks/components/stats/ManaBaseHealth';
import ManaCurveChart from '@/features/decks/components/stats/ManaCurveChart';
import TypeBreakdownChart from '@/features/decks/components/stats/TypeBreakdownChart';
import ValidationSection, { getValidationStatus } from '@/features/decks/components/stats/ValidationSection';
import {
  useActiveDeckQuery,
  useAddDeckCardMutation,
  useAddOrMergeDeckCardsMutation,
  useBulkUpdateDeckCardZonesMutation,
  useRemoveDeckCardsMutation,
  useSetActiveDeckId,
  useUpdateDeckMutation,
} from '@/features/decks/hooks/api';
import useDeckHealth from '@/features/decks/hooks/useDeckHealth';
import useDeckStatistics from '@/features/decks/hooks/useDeckStatistics';
import useManaBaseAnalysis from '@/features/decks/hooks/useManaBaseAnalysis';
import { computeBulkZoneMove, getAvailableBulkTargetZones, getDefaultBulkTargetZone } from '@/features/decks/lib/bulkZoneMove';
import type { DeckImportResult } from '@/features/decks/lib/deckImportExport';
import { enrichedDeckCardsAtom } from '@/features/decks/state/allDeckCards';
import {
  closeCardDetailsAtom,
  deckCardDetailsAtom,
  isCardDetailsPanelOpenAtom,
  openCardPreviewAtom,
  openDeckCardDetailsAtom,
} from '@/features/decks/state/deckCardDetailsAtom';
import {
  DECK_GROUP_BY_OPTIONS,
  type DeckBoard,
  deckGroupByAtom,
  deckKeywordFilterAtom,
  deckRoleFilterAtom,
  deckSearchQueryAtom,
  deckViewModeAtom,
  selectedBoardAtom,
} from '@/features/decks/state/deckEditorSettings';
import AddToListSelectModal from '@/features/lists/components/AddToListSelectModal';
import { useAddCardToList } from '@/features/lists/hooks/api';
import useUserSettings from '@/features/settings/hooks/api';
import useCardIndex from '@/hooks/useCardIndex';
import useCardLookup from '@/hooks/useCardLookup';
import { useDeckNeedsForDeck } from '@/hooks/useDeckNeeds';
import { matchesCardSearchQuery } from '@/lib/cardSearch';
import { DECK_FORMAT_LABELS, DECK_STATUS_LABELS, DECK_ZONE_LABELS, isCommanderFormat } from '@/lib/deckUtils';
import { getDefaultZoneForCard, validateDeck } from '@/lib/deckValidation';
import { cardIndexByIdAtom } from '@/services/cardDatabase';
import { collectionOwnedByOracleIdAtom } from '@/state/cardAvailability';
import { fullCardCacheAtom } from '@/state/cardCache';
import type { Deck, DeckFormat, DeckStatus, DeckZone, EnrichedDeckCard } from '@/types/deck';
import DeckListView from './components/DeckListView';
import useSetCommander from './hooks/useSetCommander';

// ============================================================================
// Types
// ============================================================================

/** Zones that belong to each board */
const BOARD_ZONES: Record<DeckBoard, DeckZone[]> = {
  main: ['commander', 'companion', 'main'],
  sideboard: ['sideboard'],
  maybeboard: ['maybeboard'],
  tokens: ['tokens'],
};

// ============================================================================
// Helpers
// ============================================================================

/**
 * Auto-select a cover card for a deck.
 * - For commander formats: use the commander card
 * - Otherwise: first non-land card sorted by name
 */
function selectDefaultCoverCard(enrichedCards: EnrichedDeckCard[], format: DeckFormat | null): string | null {
  if (enrichedCards.length === 0) return null;

  // For commander formats, prefer the commander
  if (isCommanderFormat(format)) {
    const commander = enrichedCards.find((c) => c.zone === 'commander');
    if (commander) return commander.scryfall_id;
  }

  // Otherwise, pick first non-land card sorted by name
  const nonLandCards = enrichedCards.filter((c) => !c.classification.is_land).sort((a, b) => a.card.name.localeCompare(b.card.name));

  if (nonLandCards.length > 0) {
    return nonLandCards[0].scryfall_id;
  }

  // Fallback to first card with data
  const firstWithCard = enrichedCards.find((c) => c.card);
  return firstWithCard?.scryfall_id ?? null;
}

// ============================================================================
// Inline Editable Description Component
// ============================================================================

interface InlineEditDescriptionProps {
  value: string | null;
  onSave: (description: string | null) => void;
  isLoading?: boolean;
  isLocked?: boolean;
}

function InlineEditDescription({ value, onSave, isLoading, isLocked }: InlineEditDescriptionProps): ReactElement {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value ?? '');

  const hasDescription = value && value.trim().length > 0;
  const canEdit = !isLocked && !isLoading;

  const handleStartEdit = useCallback(() => {
    if (!canEdit) return;
    setEditValue(value ?? '');
    setIsEditing(true);
  }, [canEdit, value]);

  const handleSave = useCallback(() => {
    const trimmed = editValue.trim();
    const newValue = trimmed.length > 0 ? trimmed : null;
    if (newValue !== value) {
      onSave(newValue);
    }
    setIsEditing(false);
  }, [editValue, value, onSave]);

  const handleCancel = useCallback(() => {
    setEditValue(value ?? '');
    setIsEditing(false);
  }, [value]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Escape') {
        handleCancel();
      } else if (e.key === 'Enter' && e.ctrlKey) {
        e.preventDefault();
        handleSave();
      }
    },
    [handleCancel, handleSave],
  );

  if (isEditing) {
    return (
      <div className="flex items-start gap-2">
        <textarea
          value={editValue}
          onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Add a description..."
          maxLength={500}
          rows={2}
          autoFocus
          className="focus:border-aether flex-1 resize-none rounded border border-zinc-600 bg-zinc-800 px-2 py-1 text-xs text-zinc-300 placeholder:text-zinc-600 focus:outline-none"
        />
        <button
          type="button"
          onClick={handleSave}
          disabled={isLoading}
          className="rounded p-1.5 text-emerald-400 transition-colors hover:bg-zinc-700"
          aria-label="Save description"
        >
          <FaCheck className="h-3 w-3" />
        </button>
        <button
          type="button"
          onClick={handleCancel}
          className="rounded p-1.5 text-zinc-400 transition-colors hover:bg-zinc-700"
          aria-label="Cancel edit"
        >
          <FaTimes className="h-3 w-3" />
        </button>
      </div>
    );
  }

  if (hasDescription) {
    return (
      <button
        type="button"
        onClick={handleStartEdit}
        disabled={!canEdit}
        className={clsx(
          'group -mx-2 -my-0.5 flex items-center gap-2 rounded px-2 py-0.5 text-left transition-colors',
          canEdit && 'hover:bg-zinc-800/50',
        )}
      >
        <p className="line-clamp-1 text-xs text-zinc-400">{value}</p>
        {canEdit && <FaEdit className="h-3 w-3 shrink-0 text-zinc-500 opacity-0 transition-opacity group-hover:opacity-100" />}
      </button>
    );
  }

  // No description - show placeholder
  if (canEdit) {
    return (
      <button
        type="button"
        onClick={handleStartEdit}
        className="group -mx-2 -my-0.5 flex items-center gap-1.5 rounded px-2 py-0.5 text-left text-xs text-zinc-600 transition-colors hover:bg-zinc-800/50 hover:text-zinc-500"
      >
        <FaEdit className="h-3 w-3" />
        <span>Add description...</span>
      </button>
    );
  }

  return <span className="text-xs text-zinc-600 italic">No description</span>;
}

// ============================================================================
// Format Selector Component (Compact for Header)
// ============================================================================

const FORMAT_OPTIONS: DropdownOption<DeckFormat>[] = [
  { value: 'standard', label: DECK_FORMAT_LABELS.standard },
  { value: 'pioneer', label: DECK_FORMAT_LABELS.pioneer },
  { value: 'modern', label: DECK_FORMAT_LABELS.modern },
  { value: 'legacy', label: DECK_FORMAT_LABELS.legacy },
  { value: 'vintage', label: DECK_FORMAT_LABELS.vintage },
  { value: 'pauper', label: DECK_FORMAT_LABELS.pauper },
  { value: 'commander', label: DECK_FORMAT_LABELS.commander },
  { value: 'brawl', label: DECK_FORMAT_LABELS.brawl },
  { value: 'oathbreaker', label: DECK_FORMAT_LABELS.oathbreaker },
  { value: 'draft', label: DECK_FORMAT_LABELS.draft },
  { value: 'sealed', label: DECK_FORMAT_LABELS.sealed },
  { value: 'jumpstart', label: DECK_FORMAT_LABELS.jumpstart },
  { value: 'casual', label: DECK_FORMAT_LABELS.casual },
];

interface FormatSelectorProps {
  value: DeckFormat | null;
  onChange: (format: DeckFormat | null) => void;
}

function FormatSelector({ value, onChange }: FormatSelectorProps): ReactElement {
  return (
    <Dropdown<DeckFormat>
      value={value}
      options={FORMAT_OPTIONS}
      onChange={onChange}
      placeholder="No Format"
      allowEmpty
      aria-label="Deck format"
    />
  );
}

// ============================================================================
// Status Dropdown Component (Compact for Header)
// ============================================================================

interface StatusDropdownProps {
  value: DeckStatus;
  onChange: (status: DeckStatus) => void;
}

function StatusDropdown({ value, onChange }: StatusDropdownProps): ReactElement {
  const statuses: DeckStatus[] = ['brewing', 'active', 'archived'];

  const colorClasses: Record<DeckStatus, string> = {
    brewing: 'text-amber-300 bg-amber-500/20 border-amber-500/40',
    active: 'text-emerald-300 bg-emerald-500/20 border-emerald-500/40',
    archived: 'text-slate-300 bg-slate-500/20 border-slate-500/40',
  };

  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as DeckStatus)}
      className={clsx('rounded-md border px-2 py-1 text-xs font-medium focus:outline-none', colorClasses[value])}
    >
      {statuses.map((status) => (
        <option key={status} value={status} className="bg-zinc-800 text-zinc-100">
          {DECK_STATUS_LABELS[status]}
        </option>
      ))}
    </select>
  );
}

// ============================================================================
// Deck Info Panel Component
// ============================================================================

/** Collapsible section names for statistics panel */
type StatsSection = StatsSectionId;

interface DeckInfoPanelProps {
  deck: Deck;
  enrichedCards: EnrichedDeckCard[];
}

function DeckInfoPanel({ deck, enrichedCards }: DeckInfoPanelProps): ReactElement {
  // Refs for scrolling to sections
  const sectionRefs = useRef<Partial<Record<StatsSection, HTMLDivElement | null>>>({});

  // Collapsed sections state (persisted to localStorage)
  const [collapsedSections, setCollapsedSections] = useLocalStorage<Record<StatsSection, boolean>>('mtgcraft:deck-stats-collapsed', {
    summary: false,
    manaCurve: false,
    types: true,
    colors: true,
    landAnalysis: false,
    deckHealth: false,
    commanderCategories: false,
    validation: false,
  });

  const toggleSection = useCallback(
    (section: StatsSection) => {
      setCollapsedSections((prev) => ({
        ...prev,
        [section]: !prev[section],
      }));
    },
    [setCollapsedSections],
  );

  // Handle navigation from warnings - expand section and scroll to it
  const handleNavigateToSection = useCallback(
    (sectionId: StatsSection) => {
      // Expand the section if collapsed
      setCollapsedSections((prev) => ({
        ...prev,
        [sectionId]: false,
      }));

      // Scroll to section after DOM updates
      requestAnimationFrame(() => {
        const sectionEl = sectionRefs.current[sectionId];
        if (sectionEl) {
          sectionEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    },
    [setCollapsedSections],
  );

  // Compute deck statistics
  const [manaAnalysisTurn, setManaAnalysisTurn] = useState<number | undefined>(undefined);
  const stats = useDeckStatistics(enrichedCards);
  const manaAnalysis = useManaBaseAnalysis(enrichedCards, deck.format, manaAnalysisTurn);
  const deckHealth = useDeckHealth(enrichedCards, deck.format, { landRecommendation: manaAnalysis.landRecommendation });

  // Calculate validation with the enriched cards
  const validation = useMemo(() => {
    return validateDeck({
      deck,
      cards: enrichedCards,
      allowedIllegalCards: [],
    });
  }, [deck, enrichedCards]);

  // Validation status for collapsed indicator
  const validationStatus = getValidationStatus(validation, deck.format);

  // Deck health status for collapsed indicator
  const deckHealthStatus: 'valid' | 'warning' = deckHealth.warnings.length === 0 ? 'valid' : 'warning';

  return (
    <div className="flex h-full flex-col">
      {/* Statistics Sections */}
      <div className="flex-1 overflow-y-auto">
        {/* Summary Section */}
        <div
          ref={(el) => {
            sectionRefs.current.summary = el;
          }}
        >
          <CollapsibleSection<StatsSection> id="summary" label="Summary" isCollapsed={collapsedSections.summary} onToggle={toggleSection}>
            <DeckSummaryCard stats={stats} />
          </CollapsibleSection>
        </div>

        {/* Validation Section */}
        <div
          ref={(el) => {
            sectionRefs.current.validation = el;
          }}
        >
          <CollapsibleSection<StatsSection>
            id="validation"
            label="Validation"
            isCollapsed={validationStatus === 'valid' || collapsedSections.validation}
            onToggle={toggleSection}
            status={validationStatus}
            lockedCollapsed={validationStatus === 'valid'}
          >
            <ValidationSection validation={validation} format={deck.format} />
          </CollapsibleSection>
        </div>

        {/* Deck Health Section */}
        <div
          ref={(el) => {
            sectionRefs.current.deckHealth = el;
          }}
        >
          <CollapsibleSection<StatsSection>
            id="deckHealth"
            label="Deck Health"
            isCollapsed={deckHealthStatus === 'valid' || collapsedSections.deckHealth}
            onToggle={toggleSection}
            status={deckHealthStatus}
            lockedCollapsed={deckHealthStatus === 'valid'}
          >
            <DeckWarnings warnings={deckHealth.warnings} compact onNavigateToSection={handleNavigateToSection} />
          </CollapsibleSection>
        </div>

        {/* Mana Curve Section */}
        <div
          ref={(el) => {
            sectionRefs.current.manaCurve = el;
          }}
        >
          <CollapsibleSection<StatsSection>
            id="manaCurve"
            label="Mana Curve"
            isCollapsed={collapsedSections.manaCurve}
            onToggle={toggleSection}
          >
            <ManaCurveChart data={stats.manaCurve} compact />
          </CollapsibleSection>
        </div>

        {/* Types Section */}
        <div
          ref={(el) => {
            sectionRefs.current.types = el;
          }}
        >
          <CollapsibleSection<StatsSection> id="types" label="Card Types" isCollapsed={collapsedSections.types} onToggle={toggleSection}>
            <TypeBreakdownChart data={stats.typeBreakdown} compact />
          </CollapsibleSection>
        </div>

        {/* Colors Section */}
        <div
          ref={(el) => {
            sectionRefs.current.colors = el;
          }}
        >
          <CollapsibleSection<StatsSection> id="colors" label="Colors" isCollapsed={collapsedSections.colors} onToggle={toggleSection}>
            <ColorDistributionChart data={stats.colorDistribution} compact />
          </CollapsibleSection>
        </div>

        {/* Land Analysis Section */}
        <div
          ref={(el) => {
            sectionRefs.current.landAnalysis = el;
          }}
        >
          <CollapsibleSection<StatsSection>
            id="landAnalysis"
            label="Land Analysis"
            isCollapsed={collapsedSections.landAnalysis}
            onToggle={toggleSection}
          >
            <div className="space-y-4">
              <LandRecommendation data={manaAnalysis.landRecommendation} compact />
              <ColorSourceAnalysis
                data={manaAnalysis.colorSourceAnalysis}
                compact
                format={deck.format}
                targetTurn={manaAnalysisTurn}
                onTurnChange={setManaAnalysisTurn}
              />
              <ManaBaseHealth data={manaAnalysis.manaBaseHealth} compact />
            </div>
          </CollapsibleSection>
        </div>

        {/* Commander Categories Section (only for commander formats) */}
        {deckHealth.commanderCategories && (
          <div
            ref={(el) => {
              sectionRefs.current.commanderCategories = el;
            }}
          >
            <CollapsibleSection<StatsSection>
              id="commanderCategories"
              label="Commander Essentials"
              isCollapsed={collapsedSections.commanderCategories}
              onToggle={toggleSection}
            >
              <CommanderCategories categories={deckHealth.commanderCategories} compact />
            </CollapsibleSection>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export default function DeckEditorPage(): ReactElement {
  const { deckId } = useParams<{ deckId: string }>();
  const navigate = useNavigate();

  // Set the active deck ID from URL (this syncs state with the URL)
  useSetActiveDeckId(deckId ?? null);

  // State
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isPowerModalOpen, setIsPowerModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isAddMissingModalOpen, setIsAddMissingModalOpen] = useState(false);
  const [isAddingToList, setIsAddingToList] = useState(false);
  const [rightPanel, setRightPanel] = useState<'none' | 'cards' | 'probability'>('none');
  const selectedBoard = useAtomValue(selectedBoardAtom);

  // Multi-select (mass move/remove) state
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedDeckCardIds, setSelectedDeckCardIds] = useState<Set<string>>(new Set());
  const [bulkTargetZone, setBulkTargetZone] = useState<DeckZone>('main');
  const [isBulkRemoveConfirmOpen, setIsBulkRemoveConfirmOpen] = useState(false);
  const closeCardDetails = useSetAtom(closeCardDetailsAtom);

  // Card details panel state
  const cardDetailsState = useAtomValue(deckCardDetailsAtom);
  const isPanelOpen = useAtomValue(isCardDetailsPanelOpenAtom);
  const openDeckCardDetails = useSetAtom(openDeckCardDetailsAtom);
  const openCardPreview = useSetAtom(openCardPreviewAtom);
  const fullCardCache = useAtomValue(fullCardCacheAtom);

  // Get Scryfall card data for details panel
  const panelScryfallCard = useMemo(() => {
    if (!cardDetailsState.scryfallId) return null;
    return fullCardCache.get(cardDetailsState.scryfallId) ?? null;
  }, [cardDetailsState.scryfallId, fullCardCache]);

  // Track previous card count for auto-cover selection
  const prevCardCountRef = useRef<number>(0);

  // Close right panel on Escape key
  useEffect(() => {
    if (rightPanel === 'none') return;
    const handleKeyDown = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setRightPanel('none');
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [rightPanel]);

  // Mutations
  const addCardMutation = useAddDeckCardMutation();
  const addOrMergeCardsMutation = useAddOrMergeDeckCardsMutation();
  const updateDeckMutation = useUpdateDeckMutation();
  const addCardToList = useAddCardToList();
  const bulkMoveZonesMutation = useBulkUpdateDeckCardZonesMutation();
  const bulkRemoveMutation = useRemoveDeckCardsMutation();

  // Deck data
  const { deck, cards, isLoading, error } = useActiveDeckQuery();
  const isLoadingDeck = isLoading || (!deck && deckId && !error);

  // Deck needs (cards not owned)
  const missingCards = useDeckNeedsForDeck(deckId ?? '');

  const setCommander = useSetCommander({ deck, cards });

  // Get card data from database (MinimalCard - has all cards)
  const cardsById = useAtomValue(cardIndexByIdAtom);
  const viewMode = useAtomValue(deckViewModeAtom);
  const [groupBy, setGroupBy] = useAtom(deckGroupByAtom);
  const roleFilter = useAtomValue(deckRoleFilterAtom);
  const [keywordFilter, setKeywordFilter] = useAtom(deckKeywordFilterAtom);
  const [searchQuery, setSearchQuery] = useAtom(deckSearchQueryAtom);
  const isLocked = deck?.is_locked ?? false;

  // User settings for currency
  const { settings } = useUserSettings();
  const currency = settings.currency;

  // Get owned card oracle IDs for summary view highlighting
  const collectionOwnedByOracleId = useAtomValue(collectionOwnedByOracleIdAtom);
  const ownedOracleIds = useMemo(() => {
    return new Set(collectionOwnedByOracleId.keys());
  }, [collectionOwnedByOracleId]);

  // Build price map from full card cache for summary view
  const priceMap = useMemo(() => {
    const map = new Map<string, number>();
    for (const [scryfallId, card] of fullCardCache) {
      // Use currency-appropriate price field
      const priceStr = currency === 'EUR' ? card.prices?.eur : card.prices?.usd;
      const price = priceStr ? parseFloat(priceStr) : null;
      if (price !== null) {
        map.set(scryfallId, price);
      }
    }
    return map;
  }, [fullCardCache, currency]);

  // Enrich cards with full Scryfall data from cache
  // Filter out cards without cached data (non-nullable card)
  const allEnrichedDeckCards = useAtomValue(enrichedDeckCardsAtom);
  const enrichedCards = useMemo(
    (): EnrichedDeckCard[] => (deckId ? allEnrichedDeckCards.filter((c) => c.deck_id === deckId) : []),
    [allEnrichedDeckCards, deckId],
  );

  // Filter cards by selected board
  const filteredCards = useMemo((): EnrichedDeckCard[] => {
    const boardZones = BOARD_ZONES[selectedBoard];
    return enrichedCards.filter((c) => boardZones.includes(c.zone));
  }, [enrichedCards, selectedBoard]);

  // Card database for name/set lookups (used for import)
  const { getCardByName, getCardBySetNumber } = useCardIndex();
  const { prefetchCards } = useCardLookup();

  // Separate commander and companion cards from regular cards for prominent display
  const { commanderCards, companionCards, nonCommanderCards } = useMemo(() => {
    const commanders: EnrichedDeckCard[] = [];
    const companions: EnrichedDeckCard[] = [];
    const nonCommanders: EnrichedDeckCard[] = [];

    for (const card of filteredCards) {
      if (card.zone === 'commander') {
        commanders.push(card);
      } else if (card.zone === 'companion') {
        companions.push(card);
      } else {
        nonCommanders.push(card);
      }
    }

    return { commanderCards: commanders, companionCards: companions, nonCommanderCards: nonCommanders };
  }, [filteredCards]);

  // Apply role filter if any roles are selected
  const roleFilteredCards = useMemo((): EnrichedDeckCard[] => {
    if (roleFilter.length === 0) return nonCommanderCards;

    return nonCommanderCards.filter((deckCard) => {
      // Show card if it has ANY of the selected roles
      return roleFilter.some((roleId) => deckCard.classification.active_roles.includes(roleId));
    });
  }, [nonCommanderCards, roleFilter]);

  // Apply keyword filter after role filter
  const keywordFilteredCards = useMemo((): EnrichedDeckCard[] => {
    if (keywordFilter.length === 0) return roleFilteredCards;
    const kwLower = keywordFilter.map((k) => k.toLowerCase());
    return roleFilteredCards.filter((c) => {
      const cardKw = c.card.keywords?.map((k: string) => k.toLowerCase()) ?? [];
      return kwLower.some((kw) => cardKw.includes(kw));
    });
  }, [roleFilteredCards, keywordFilter]);

  // Apply search filter after keyword filter
  const searchFilteredCards = useMemo((): EnrichedDeckCard[] => {
    const queryLower = searchQuery.trim().toLowerCase();
    if (!queryLower) return keywordFilteredCards;
    return keywordFilteredCards.filter((c) => matchesCardSearchQuery(c.card, queryLower));
  }, [keywordFilteredCards, searchQuery]);

  // Filter commanders/companions by search query (roles/keywords stay commander-exempt)
  const filteredCommanderCards = useMemo((): EnrichedDeckCard[] => {
    const queryLower = searchQuery.trim().toLowerCase();
    if (!queryLower) return commanderCards;
    return commanderCards.filter((c) => matchesCardSearchQuery(c.card, queryLower));
  }, [commanderCards, searchQuery]);

  const filteredCompanionCards = useMemo((): EnrichedDeckCard[] => {
    const queryLower = searchQuery.trim().toLowerCase();
    if (!queryLower) return companionCards;
    return companionCards.filter((c) => matchesCardSearchQuery(c.card, queryLower));
  }, [companionCards, searchQuery]);

  // Shared predicate for applying all deck filters (role, keyword, search) — used by Summary view
  const matchesDeckFilters = useCallback(
    (card: EnrichedDeckCard): boolean => {
      // Role filter
      if (roleFilter.length > 0) {
        if (!roleFilter.some((roleId) => card.classification.active_roles.includes(roleId))) return false;
      }
      // Keyword filter
      if (keywordFilter.length > 0) {
        const kwLower = keywordFilter.map((k) => k.toLowerCase());
        const cardKw = card.card.keywords?.map((k: string) => k.toLowerCase()) ?? [];
        if (!kwLower.some((kw) => cardKw.includes(kw))) return false;
      }
      // Search filter
      const queryLower = searchQuery.trim().toLowerCase();
      if (queryLower && !matchesCardSearchQuery(card.card, queryLower)) return false;
      return true;
    },
    [roleFilter, keywordFilter, searchQuery],
  );

  // Separate cards by zone for summary view (shows all zones regardless of board selection)
  // Apply all filters (roles, keywords, search) so Summary view is consistent with other views
  const { mainDeckCards, sideboardCards, maybeboardCards, tokensCards } = useMemo(() => {
    const main: EnrichedDeckCard[] = [];
    const sideboard: EnrichedDeckCard[] = [];
    const maybeboard: EnrichedDeckCard[] = [];
    const tokens: EnrichedDeckCard[] = [];

    for (const card of enrichedCards) {
      if (card.zone === 'commander') continue; // handled separately
      if (!matchesDeckFilters(card)) continue;
      if (card.zone === 'sideboard') {
        sideboard.push(card);
      } else if (card.zone === 'maybeboard') {
        maybeboard.push(card);
      } else if (card.zone === 'tokens') {
        tokens.push(card);
      } else {
        main.push(card); // main, companion
      }
    }

    return { mainDeckCards: main, sideboardCards: sideboard, maybeboardCards: maybeboard, tokensCards: tokens };
  }, [enrichedCards, matchesDeckFilters]);

  // Handle setting a card as cover
  const handleSetCover = useCallback(
    (scryfallId: string) => {
      if (!deck) return;
      updateDeckMutation.mutate({
        id: deck.id,
        data: { cover_card_id: scryfallId },
      });
    },
    [deck, updateDeckMutation],
  );

  // Handle locking/unlocking the deck

  // Get enriched collection for import matching
  const enrichedCollection = useAtomValue(enrichedCollectionAtom);

  // Handle importing cards into the current deck
  const handleImportCards = useCallback(
    async (result: DeckImportResult) => {
      if (!deck) return;

      // Build collection lookup for prioritizing owned versions
      // Map card name (lowercase) -> scryfallId of owned printing
      const collectionByName = new Map<string, string>();
      if (enrichedCollection.data) {
        for (const cc of enrichedCollection.data) {
          const nameLower = cc.card.name.toLowerCase();
          if (!collectionByName.has(nameLower)) {
            collectionByName.set(nameLower, cc.scryfall_id);
          }
        }
      }

      // Collect scryfall IDs of newly added cards for prefetch
      const addedScryfallIds: string[] = [];
      // Collect resolved card inputs; added in one merge-aware batch below.
      const cardInputs: { scryfall_id: string; quantity: number; zone: DeckZone }[] = [];

      // Resolve each card from the import
      for (const importCard of result.cards) {
        let scryfallId = importCard.scryfallId;

        // If no scryfallId, try to look up the card
        if (!scryfallId) {
          // Case 1: Has set + collector number - prefer exact printing
          if (importCard.setCode && importCard.collectorNumber) {
            const matched = getCardBySetNumber(importCard.setCode, importCard.collectorNumber);
            if (matched) {
              scryfallId = matched.id;
            } else {
              // Set/number not found, fallback to name match
              const nameMatched = getCardByName(importCard.name);
              if (nameMatched) {
                scryfallId = nameMatched.id;
              }
            }
          } else {
            // Case 2: Name only - prioritize owned version
            const ownedVersion = collectionByName.get(importCard.name.toLowerCase());
            if (ownedVersion) {
              // User owns this card - use their owned printing
              scryfallId = ownedVersion;
            } else {
              // User doesn't own this card - use any printing from database
              const matched = getCardByName(importCard.name);
              if (matched) {
                scryfallId = matched.id;
              }
            }
          }
        }

        if (scryfallId) {
          addedScryfallIds.push(scryfallId);

          // Check if the matched card is a token/emblem - override zone to 'tokens' only for those
          const matchedCard = cardsById.get(scryfallId);
          const defaultZone = matchedCard ? getDefaultZoneForCard(matchedCard) : null;
          // Use 'tokens' zone for tokens/emblems, otherwise respect the import zone
          const zone = defaultZone === 'tokens' ? 'tokens' : importCard.zone;

          cardInputs.push({ scryfall_id: scryfallId, quantity: importCard.quantity, zone });
        }
      }

      // Add all resolved cards in one batch; duplicates merge into existing slots.
      if (cardInputs.length > 0) {
        await addOrMergeCardsMutation.mutateAsync({ deckId: deck.id, cards: cardInputs });
      }

      // Prefetch full card data so imported cards appear immediately
      if (addedScryfallIds.length > 0) {
        await prefetchCards(addedScryfallIds);
      }

      // Close import modal
      setIsImportModalOpen(false);
    },
    [deck, addOrMergeCardsMutation, getCardByName, getCardBySetNumber, enrichedCollection.data, prefetchCards],
  );
  const handleToggleLock = useCallback(() => {
    if (!deck) return;
    updateDeckMutation.mutate({
      id: deck.id,
      data: { is_locked: !deck.is_locked },
    });
  }, [deck, updateDeckMutation]);

  // Handle deck property changes (moved from DeckInfoPanel)
  const handleNameChange = useCallback(
    (name: string) => {
      if (!deck) return;
      updateDeckMutation.mutate({ id: deck.id, data: { name } });
    },
    [updateDeckMutation, deck],
  );

  const handleFormatChange = useCallback(
    (format: DeckFormat | null) => {
      if (!deck) return;
      updateDeckMutation.mutate({ id: deck.id, data: { format } });
    },
    [updateDeckMutation, deck],
  );

  const handleStatusChange = useCallback(
    (status: DeckStatus) => {
      if (!deck) return;
      updateDeckMutation.mutate({ id: deck.id, data: { status } });
    },
    [updateDeckMutation, deck],
  );

  // Handle description change
  const handleDescriptionChange = useCallback(
    (description: string | null) => {
      if (!deck) return;
      updateDeckMutation.mutate({ id: deck.id, data: { description } });
    },
    [updateDeckMutation, deck],
  );

  // Handle adding all missing cards to a list
  const handleAddMissingToList = useCallback(
    async (listId: string) => {
      if (!deck || missingCards.length === 0) return;

      setIsAddingToList(true);
      try {
        // Add each missing card to the list
        for (const card of missingCards) {
          await addCardToList({
            list_id: listId,
            oracle_id: card.oracle_id,
            name: card.name,
            quantity: card.shortfall,
            priority: 'medium',
          });
        }
      } finally {
        setIsAddingToList(false);
      }
    },
    [deck, missingCards, addCardToList],
  );

  // Handle successful delete - navigate to decks list
  const handleDeleteSuccess = useCallback(() => {
    navigate('/decks');
  }, [navigate]);

  // Handle setting a card as commander
  const handleSetCommander = useCallback(
    (cardId: string) => {
      setCommander.setCommander(cardId);
    },
    [setCommander],
  );

  // Handle card selection from deck list (opens panel in "in-deck" mode)
  const handleDeckCardSelect = useCallback(
    (scryfallId: string, deckCardId: string) => {
      openDeckCardDetails({ scryfallId, deckCardId });
    },
    [openDeckCardDetails],
  );

  // ===== Multi-select (mass move/remove) =====

  // Zones available as bulk-move targets for this deck
  const availableBulkZones = useMemo(() => (deck ? getAvailableBulkTargetZones(deck.format, deck.status) : []), [deck]);

  // Keep the bulk target zone valid and defaulted to a sensible zone for the board
  useEffect(() => {
    if (!isSelectionMode || !deck) return;
    setBulkTargetZone((prev) =>
      availableBulkZones.includes(prev) ? prev : getDefaultBulkTargetZone(selectedBoard, deck.format, deck.status),
    );
  }, [isSelectionMode, deck, selectedBoard, availableBulkZones]);

  const handleClearSelection = useCallback(() => {
    setSelectedDeckCardIds(new Set());
  }, []);

  const handleToggleSelectionMode = useCallback(() => {
    setIsSelectionMode((prev) => {
      const next = !prev;
      if (next) {
        // Entering selection mode: close the details panel to avoid conflicting clicks
        closeCardDetails();
      } else {
        setSelectedDeckCardIds(new Set());
      }
      return next;
    });
  }, [closeCardDetails]);

  const handleToggleDeckCard = useCallback((deckCardId: string) => {
    setSelectedDeckCardIds((prev) => {
      const next = new Set(prev);
      if (next.has(deckCardId)) next.delete(deckCardId);
      else next.add(deckCardId);
      return next;
    });
  }, []);

  const handleSelectAllVisible = useCallback(() => {
    setSelectedDeckCardIds(new Set(searchFilteredCards.map((c) => c.id)));
  }, [searchFilteredCards]);

  const handleBulkMove = useCallback(() => {
    if (!deck || selectedDeckCardIds.size === 0) return;
    const selected = enrichedCards.filter((c) => selectedDeckCardIds.has(c.id));
    const { legal, skipped } = computeBulkZoneMove(selected, bulkTargetZone, deck.format, deck.status);

    if (legal.length === 0) {
      toast.error(skipped.length > 0 ? `No cards could move to ${DECK_ZONE_LABELS[bulkTargetZone]}` : 'Nothing to move');
      return;
    }

    bulkMoveZonesMutation.mutate(
      { deckId: deck.id, updates: legal },
      {
        onSuccess: () => {
          const movedMsg = `Moved ${legal.length.toLocaleString()} ${legal.length === 1 ? 'card' : 'cards'} to ${DECK_ZONE_LABELS[bulkTargetZone]}`;
          if (skipped.length > 0) {
            toast.success(`${movedMsg} · skipped ${skipped.length.toLocaleString()}`);
          } else {
            toast.success(movedMsg);
          }
          setSelectedDeckCardIds(new Set());
        },
        onError: (err) => {
          toast.error(`Failed to move cards: ${err instanceof Error ? err.message : 'Unknown error'}`);
        },
      },
    );
  }, [deck, selectedDeckCardIds, enrichedCards, bulkTargetZone, bulkMoveZonesMutation]);

  const handleConfirmBulkRemove = useCallback(() => {
    if (!deck || selectedDeckCardIds.size === 0) return;
    const ids = Array.from(selectedDeckCardIds);
    bulkRemoveMutation.mutate(
      { deckId: deck.id, cardIds: ids },
      {
        onSuccess: () => {
          toast.success(`Removed ${ids.length.toLocaleString()} ${ids.length === 1 ? 'card' : 'cards'} from deck`);
          setSelectedDeckCardIds(new Set());
          setIsBulkRemoveConfirmOpen(false);
        },
        onError: (err) => {
          toast.error(`Failed to remove cards: ${err instanceof Error ? err.message : 'Unknown error'}`);
        },
      },
    );
  }, [deck, selectedDeckCardIds, bulkRemoveMutation]);

  // Clear selection when switching boards
  useEffect(() => {
    setSelectedDeckCardIds(new Set());
  }, [selectedBoard]);

  // Exit selection mode when switching to a view that doesn't support it (stack/summary)
  useEffect(() => {
    if (isSelectionMode && viewMode !== 'list' && viewMode !== 'grid') {
      setIsSelectionMode(false);
      setSelectedDeckCardIds(new Set());
    }
  }, [viewMode, isSelectionMode]);

  // Exit selection mode on Escape
  useEffect(() => {
    if (!isSelectionMode) return;
    const handleKeyDown = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setIsSelectionMode(false);
        setSelectedDeckCardIds(new Set());
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isSelectionMode]);

  // Handle card selection from search (opens panel in "preview" mode)
  const handleSearchCardSelect = useCallback(
    (scryfallId: string) => {
      openCardPreview(scryfallId);
    },
    [openCardPreview],
  );

  // Handle adding card from details panel
  const handleAddCardFromPanel = useCallback(
    (scryfallId: string) => {
      if (!deck) return;
      const cardData = cardsById.get(scryfallId);
      const zone = getDefaultZoneForCard(cardData);
      addCardMutation.mutate({
        deckId: deck.id,
        card: {
          scryfall_id: scryfallId,
          card_name: cardData?.name ?? null,
          quantity: 1,
          zone,
        },
      });
    },
    [deck, cardsById, addCardMutation],
  );

  // Moves current commander to main deck if one exists

  // Auto-select cover card when cards are added and no cover is set
  useEffect(() => {
    if (!deck || deck.cover_card_id || enrichedCards.length === 0) {
      prevCardCountRef.current = enrichedCards.length;
      return;
    }

    // Only auto-select if cards were just added (count increased from 0)
    const wasEmpty = prevCardCountRef.current === 0;
    prevCardCountRef.current = enrichedCards.length;

    if (wasEmpty && enrichedCards.length > 0) {
      const defaultCover = selectDefaultCoverCard(enrichedCards, deck.format);
      if (defaultCover) {
        updateDeckMutation.mutate({
          id: deck.id,
          data: { cover_card_id: defaultCover },
        });
      }
    }
  }, [deck, enrichedCards, updateDeckMutation]);

  // Loading state - also show while waiting for active deck to sync from URL
  if (isLoadingDeck) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  // Error or not found state (only when deck truly doesn't exist)
  if (error || !deck) {
    return (
      <div className="flex h-full flex-col items-center justify-center p-8">
        <div className="max-w-md text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-500/10">
            <FaTimes className="h-6 w-6 text-red-400" />
          </div>
          <h2 className="mb-2 text-xl font-semibold text-zinc-100">{error ? 'Error loading deck' : 'Deck not found'}</h2>
          <p className="mb-6 text-zinc-500">{error?.message ?? 'The deck you are looking for does not exist or has been deleted.'}</p>
          <Button text="Back to Decks" icon={<FaArrowLeft />} onClick={() => navigate('/decks')} />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col bg-zinc-950">
      {/* Header */}
      <header className="flex items-center gap-4 border-b border-zinc-800 bg-zinc-900/50 px-4 py-3">
        <button
          type="button"
          onClick={() => navigate('/decks')}
          className="rounded-lg p-2 text-zinc-400 transition-colors hover:bg-zinc-800 hover:text-zinc-200"
          aria-label="Back to decks"
        >
          <FaArrowLeft className="h-4 w-4" />
        </button>

        {/* Deck Name & Description (editable) */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3">
            <InlineEditText
              value={deck.name}
              onCommit={(name) => name && handleNameChange(name)}
              ariaLabel="Deck name"
              disabled={updateDeckMutation.isPending}
              maxLength={100}
              className="w-auto! max-w-full truncate text-lg! font-semibold text-zinc-100!"
            />
            <span className="text-xs text-zinc-500">{enrichedCards.reduce((sum, c) => sum + c.quantity, 0)} cards</span>
            {deck.ai_assessment && <DeckAiBadge assessment={deck.ai_assessment} onClick={() => setIsPowerModalOpen(true)} />}
          </div>
          <InlineEditDescription
            value={deck.description}
            onSave={handleDescriptionChange}
            isLoading={updateDeckMutation.isPending}
            isLocked={isLocked}
          />
        </div>

        {/* Format Selector (center area) */}
        <FormatSelector value={deck.format} onChange={handleFormatChange} />

        {/* Right side controls */}
        <div className="flex items-center gap-2">
          {/* Status Dropdown */}
          <StatusDropdown value={deck.status} onChange={handleStatusChange} />

          {/* Lock Toggle */}
          <button
            type="button"
            onClick={handleToggleLock}
            className={clsx(
              'rounded-lg p-2 transition-colors hover:bg-zinc-800',
              isLocked ? 'text-amber-400 hover:text-amber-300' : 'text-zinc-400 hover:text-zinc-200',
            )}
            aria-label={isLocked ? 'Unlock deck' : 'Lock deck'}
            title={isLocked ? 'Unlock deck (allow editing)' : 'Lock deck (prevent editing)'}
          >
            {isLocked ? <FaLock className="h-4 w-4" /> : <FaLockOpen className="h-4 w-4" />}
          </button>

          {/* Import/Export Buttons */}
          {!isLocked && (
            <Button
              icon={<FaFileImport />}
              text="Import"
              variant="outlined"
              color="secondary"
              size="small"
              onClick={() => setIsImportModalOpen(true)}
            />
          )}
          <Button
            icon={<FaFileExport />}
            text="Export"
            variant="outlined"
            color="secondary"
            size="small"
            onClick={() => setIsExportModalOpen(true)}
          />

          <Button
            icon={<IoSparkles />}
            text="Assess Power"
            variant="outlined"
            color="secondary"
            size="small"
            onClick={() => setIsPowerModalOpen(true)}
            disabled={enrichedCards.length === 0}
            title={enrichedCards.length === 0 ? 'Add cards to the deck before assessing it' : undefined}
          />

          {/* Divider */}
          <div className="h-6 w-px bg-zinc-700" />

          {/* Add Missing to List */}
          {missingCards.length > 0 && (
            <Button
              icon={<FaList />}
              text={`${missingCards.length} Missing`}
              variant="outlined"
              color="secondary"
              size="small"
              onClick={() => setIsAddMissingModalOpen(true)}
              disabled={isAddingToList}
            />
          )}

          {/* Delete */}
          <Button
            icon={<FaTrash />}
            text="Delete"
            variant="outlined"
            color="delete"
            size="small"
            onClick={() => setIsDeleteModalOpen(true)}
          />
        </div>
      </header>

      {/* Main Content - 3 Column Layout */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left Panel - Deck Stats */}
        <aside className="w-72 shrink-0 overflow-y-auto border-r border-zinc-800 bg-zinc-900/30">
          <DeckInfoPanel deck={deck} enrichedCards={enrichedCards} />
        </aside>

        {/* Center Panel - Card List */}
        <main className="flex flex-1 flex-col overflow-hidden">
          {/* View Controls */}
          <ViewControlsBar
            left={<BoardSelector cards={cards} format={deck.format} status={deck.status} />}
            right={
              <>
                {viewMode === 'list' && <ColumnVisibilityDropdown />}
                <RoleFilterDropdown />
                <KeywordFilterDropdown
                  selectedKeywords={keywordFilter}
                  onToggle={(kw) => setKeywordFilter((prev) => (prev.includes(kw) ? prev.filter((k) => k !== kw) : [...prev, kw]))}
                  onClear={() => setKeywordFilter([])}
                />
                <Search onSearch={setSearchQuery} placeholder="Search cards…" ariaLabel="Search deck cards" className="w-48" />
                <GroupBySelector value={groupBy} onChange={setGroupBy} options={DECK_GROUP_BY_OPTIONS} />
                <DeckViewToggle />
                {/* Multi-select toggle (list/grid only) */}
                {!isLocked && (viewMode === 'list' || viewMode === 'grid') && (
                  <Button
                    icon={<FaCheckSquare />}
                    text="Select"
                    size="small"
                    color={isSelectionMode ? 'primary' : 'secondary'}
                    onClick={handleToggleSelectionMode}
                  />
                )}
                {/* Right panel toggle buttons */}
                <Button
                  icon={<IoCalculator />}
                  text="Probability"
                  size="small"
                  color={rightPanel === 'probability' ? 'primary' : 'secondary'}
                  onClick={() => setRightPanel(rightPanel === 'probability' ? 'none' : 'probability')}
                />
                {!isLocked && (
                  <Button
                    icon={<FaPlus />}
                    text="Add Cards"
                    size="small"
                    color={rightPanel === 'cards' ? 'primary' : 'secondary'}
                    onClick={() => setRightPanel(rightPanel === 'cards' ? 'none' : 'cards')}
                  />
                )}
              </>
            }
          />

          {/* Multi-select action bar */}
          {isSelectionMode && (viewMode === 'list' || viewMode === 'grid') && (
            <DeckBulkActionBar
              selectedCount={selectedDeckCardIds.size}
              totalCount={searchFilteredCards.length}
              availableZones={availableBulkZones}
              targetZone={bulkTargetZone}
              onTargetZoneChange={setBulkTargetZone}
              onSelectAll={handleSelectAllVisible}
              onClearSelection={handleClearSelection}
              onMove={handleBulkMove}
              onRemove={() => setIsBulkRemoveConfirmOpen(true)}
              onClose={handleToggleSelectionMode}
              isPending={bulkMoveZonesMutation.isPending || bulkRemoveMutation.isPending}
            />
          )}

          {/* Card List Content */}
          <div className="flex-1 overflow-y-auto p-4">
            {cards.length === 0 ? (
              <div className="flex h-full flex-col items-center justify-center text-center">
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-zinc-800/50">
                  <FaPlus className="h-6 w-6 text-zinc-600" />
                </div>
                <h3 className="mb-2 text-lg font-medium text-zinc-300">
                  {selectedBoard === 'main' ? 'No cards yet' : `No ${selectedBoard} cards`}
                </h3>
                <p className="max-w-xs text-sm text-zinc-500">
                  {isLocked ? 'Unlock the deck to add cards' : 'Use the card search to add cards to your deck'}
                </p>
                {!isLocked && rightPanel !== 'cards' && (
                  <Button icon={<FaPlus />} text="Add Cards" className="mt-4" onClick={() => setRightPanel('cards')} />
                )}
              </div>
            ) : viewMode === 'grid' ? (
              <DeckGridView
                deckId={deck.id}
                cards={searchFilteredCards}
                commanderCards={filteredCommanderCards}
                companionCards={filteredCompanionCards}
                isLocked={isLocked}
                deckFormat={deck.format}
                deckStatus={deck.status}
                coverCardId={deck.cover_card_id}
                onSetCover={handleSetCover}
                onSetCommander={handleSetCommander}
                onCardSelect={handleDeckCardSelect}
                isSelectionMode={isSelectionMode}
                selectedDeckCardIds={selectedDeckCardIds}
                onToggleDeckCard={handleToggleDeckCard}
              />
            ) : viewMode === 'stack' ? (
              <DeckStackView
                deckId={deck.id}
                cards={searchFilteredCards}
                commanderCards={filteredCommanderCards}
                companionCards={filteredCompanionCards}
                isLocked={isLocked}
                deckFormat={deck.format}
                onCardSelect={handleDeckCardSelect}
              />
            ) : viewMode === 'summary' ? (
              <DeckSummaryView
                cards={mainDeckCards}
                commanderCards={filteredCommanderCards}
                sideboardCards={sideboardCards}
                maybeboardCards={maybeboardCards}
                tokensCards={tokensCards}
                onCardClick={handleDeckCardSelect}
                ownedOracleIds={ownedOracleIds}
                priceMap={priceMap}
                currency={currency}
              />
            ) : (
              <DeckListView
                selectedBoard={selectedBoard}
                deck={deck}
                cards={searchFilteredCards}
                commanderCards={filteredCommanderCards}
                companionCards={filteredCompanionCards}
                isLocked={isLocked}
                onSetCommander={handleSetCommander}
                onSetCover={handleSetCover}
                onCardSelect={handleDeckCardSelect}
                isSelectionMode={isSelectionMode}
                selectedDeckCardIds={selectedDeckCardIds}
                onToggleDeckCard={handleToggleDeckCard}
              />
            )}
          </div>
        </main>

        {/* Right Panel - Card Discovery or Probability Tools */}
        {rightPanel !== 'none' && (
          <aside className="w-80 shrink-0 overflow-hidden border-l border-zinc-800 bg-zinc-900/30 xl:w-96 2xl:w-110">
            {rightPanel === 'cards' && !isLocked ? (
              <CardDiscoveryPanel
                deckId={deck.id}
                deckFormat={deck.format}
                deckStatus={deck.status}
                onClose={() => setRightPanel('none')}
                onCardSelect={handleSearchCardSelect}
                onAddCard={(scryfallId, quantity, zone) => {
                  // Override zone to 'tokens' for tokens/emblems
                  const cardData = cardsById.get(scryfallId);
                  const effectiveZone = getDefaultZoneForCard(cardData) === 'tokens' ? 'tokens' : zone;

                  // The add service enforces the one-slot-per-(edition, zone) rule,
                  // merging into an existing slot when present.
                  addCardMutation.mutate({
                    deckId: deck.id,
                    card: {
                      scryfall_id: scryfallId,
                      card_name: cardData?.name ?? null,
                      quantity,
                      zone: effectiveZone,
                    },
                  });
                }}
              />
            ) : rightPanel === 'probability' ? (
              <DeckProbabilityPanel cards={enrichedCards} format={deck.format} onClose={() => setRightPanel('none')} />
            ) : null}
          </aside>
        )}

        {/* Card Details Panel */}
        <DeckCardDetailsPanel isOpen={isPanelOpen} scryfallCard={panelScryfallCard} onAddToDeck={handleAddCardFromPanel} />
      </div>

      {/* Export Modal */}
      <ExportDeckModal isOpen={isExportModalOpen} onClose={() => setIsExportModalOpen(false)} cards={enrichedCards} deck={deck} />

      {/* AI Power Assessment Modal */}
      <DeckPowerModal isOpen={isPowerModalOpen} onClose={() => setIsPowerModalOpen(false)} deck={deck} cards={enrichedCards} />

      {/* Import Modal */}
      <ImportDeckModal isOpen={isImportModalOpen} onClose={() => setIsImportModalOpen(false)} onImport={handleImportCards} />

      {/* Delete Deck Modal */}
      <DeleteDeckModal isOpen={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)} onSuccess={handleDeleteSuccess} deck={deck} />

      {/* Add Missing to List Modal */}
      <AddToListSelectModal
        isOpen={isAddMissingModalOpen}
        onClose={() => setIsAddMissingModalOpen(false)}
        onSelect={(listId) => {
          setIsAddMissingModalOpen(false);
          handleAddMissingToList(listId);
        }}
        title={`Add ${missingCards.length} Missing Cards to List`}
      />

      {/* Bulk Remove Confirmation */}
      <ConfirmActionModal
        isOpen={isBulkRemoveConfirmOpen}
        onClose={() => setIsBulkRemoveConfirmOpen(false)}
        title="Remove cards from deck?"
        description={`Remove ${selectedDeckCardIds.size.toLocaleString()} selected ${selectedDeckCardIds.size === 1 ? 'card' : 'cards'} (all copies) from this deck? This cannot be undone.`}
        confirmLabel="Remove"
        pendingLabel="Removing…"
        isPending={bulkRemoveMutation.isPending}
        onConfirm={handleConfirmBulkRemove}
      />
    </div>
  );
}
